import React, { useEffect, useRef, useState } from 'react';
import './VirtualKeyboard.css';

const KEYBOARD_LAYOUTS = {
  latin: [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
  ],
  vi: [
    ['ă', 'â', 'ê', 'ô', 'ơ', 'ư', 'đ', 'á', 'à', 'ả'],
    ['ã', 'ạ', 'é', 'è', 'ẻ', 'ẽ', 'ẹ', 'í', 'ì', 'ỉ'],
    ['ĩ', 'ị', 'ó', 'ò', 'ỏ', 'õ', 'ọ', 'ú', 'ù', 'ủ'],
    ['ũ', 'ụ', 'ý', 'ỳ', 'ỷ', 'ỹ', 'ỵ'],
  ],
  ar: [
    ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه'],
    ['خ', 'ح', 'ج', 'ش', 'س', 'ي', 'ب', 'ل'],
    ['ا', 'ت', 'ن', 'م', 'ك', 'ط', 'ئ', 'ء'],
    ['ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ'],
  ],
};

function VirtualKeyboard({ layout = 'latin', onInsert, onBackspace, onSpace, onClose }) {
  const rows = KEYBOARD_LAYOUTS[layout] || KEYBOARD_LAYOUTS.latin;
  const [position, setPosition] = useState({ x: 32, y: 120 });
  const dragStateRef = useRef(null);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current) return;
      const { offsetX, offsetY } = dragStateRef.current;
      const maxX = Math.max(8, window.innerWidth - 520);
      const maxY = Math.max(8, window.innerHeight - 320);
      const nextX = Math.min(Math.max(8, event.clientX - offsetX), maxX);
      const nextY = Math.min(Math.max(8, event.clientY - offsetY), maxY);
      setPosition({ x: nextX, y: nextY });
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const handlePointerDown = (event) => {
    const panelRect = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!panelRect) return;
    dragStateRef.current = {
      offsetX: event.clientX - panelRect.left,
      offsetY: event.clientY - panelRect.top,
    };
  };

  return (
    <div className="virtual-keyboard-floating" style={{ left: `${position.x}px`, top: `${position.y}px` }}>
      <div className="virtual-keyboard-header" onPointerDown={handlePointerDown}>
        <div className="virtual-keyboard-title-group">
          <span className="virtual-keyboard-title">Virtual Keyboard</span>
          <span className="virtual-keyboard-layout">{layout.toUpperCase()}</span>
        </div>
        <button type="button" className="virtual-keyboard-close" onClick={onClose} aria-label="Close virtual keyboard">
          ×
        </button>
      </div>
      <div className="virtual-keyboard-panel">
      <div className="virtual-keyboard-rows">
        {rows.map((row, rowIndex) => (
          <div className="virtual-keyboard-row" key={`row-${rowIndex}`}>
            {row.map((key) => (
              <button
                key={key}
                type="button"
                className="virtual-keyboard-key"
                onClick={() => onInsert(key)}
              >
                {key}
              </button>
            ))}
          </div>
        ))}
        <div className="virtual-keyboard-row virtual-keyboard-row--actions">
          <button type="button" className="virtual-keyboard-key virtual-keyboard-key--action" onClick={onSpace}>
            Space
          </button>
          <button type="button" className="virtual-keyboard-key virtual-keyboard-key--action" onClick={onBackspace}>
            Delete
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default VirtualKeyboard;
